import React, { useState, useEffect } from 'react';
import { ExternalLink, Linkedin, Mail, Phone, Menu, X } from 'lucide-react';
import { PROFILE } from '../../data/mock';
import { useIsMobile } from '../../hooks/useWindowSize';
import HeroSection from './HeroSection';
import StorySection from './StorySection';
import ImpactSection from './ImpactSection';
import RecognitionSection from './RecognitionSection';
import TEDxSection from './TEDxSection';

const NAV_LINKS = [
  { label: 'Story', href: '#about' },
  { label: 'Ventures', href: '#ventures' },
  { label: 'Recognition', href: '#recognition' },
  { label: 'Impact', href: '#impact' },
  { label: 'TEDx', href: '#tedx' },
  { label: 'Contact', href: '#contact' }
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: (scrolled || menuOpen) ? 'rgba(10,10,10,0.97)' : 'transparent',
        borderBottom: scrolled ? '1px solid #1A1A1A' : '1px solid transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        transition: 'background 0.35s ease, border-color 0.35s ease'
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          padding: isMobile ? '0 20px' : '0 48px',
          height: '68px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <a
            href="#home"
            onClick={e => handleNav(e, '#home')}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <div style={{
              width: '30px', height: '30px', background: '#E62B1E',
              borderRadius: '4px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0
            }}>
              <span style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '17px', color: '#fff' }}>K</span>
            </div>
            <span style={{
              fontFamily: '"Bebas Neue", sans-serif',
              fontSize: isMobile ? '14px' : '17px', color: '#FFFFFF',
              letterSpacing: '2px', whiteSpace: 'nowrap'
            }}>KRUPAL CHAUDHARY</span>
          </a>

          {/* Desktop nav */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={e => handleNav(e, link.href)}
                  style={{
                    fontFamily: 'Inter, sans-serif', fontSize: '13px',
                    fontWeight: 500, color: '#7A7A7A',
                    textDecoration: 'none', letterSpacing: '0.3px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#FFFFFF'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#7A7A7A'; }}
                >{link.label}</a>
              ))}
              <div style={{ background: '#E62B1E', borderRadius: '4px', padding: '7px 14px' }}>
                <span style={{
                  fontFamily: 'Inter, sans-serif', fontSize: '11px',
                  fontWeight: 700, color: '#fff', letterSpacing: '1.5px', textTransform: 'uppercase'
                }}>TEDx 2026</span>
              </div>
            </div>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ background: '#E62B1E', borderRadius: '4px', padding: '5px 10px' }}>
                <span style={{
                  fontFamily: 'Inter, sans-serif', fontSize: '10px',
                  fontWeight: 700, color: '#fff', letterSpacing: '1.5px', textTransform: 'uppercase'
                }}>TEDx</span>
              </div>
              <button
                onClick={() => setMenuOpen(o => !o)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#FFFFFF', padding: '4px', display: 'flex', alignItems: 'center'
                }}
              >
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          )}
        </div>

        {/* Mobile dropdown menu */}
        {isMobile && menuOpen && (
          <div style={{
            background: 'rgba(10,10,10,0.98)',
            borderTop: '1px solid #1A1A1A',
            padding: '16px 20px 24px'
          }}>
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={e => handleNav(e, link.href)}
                style={{
                  display: 'block',
                  fontFamily: 'Inter, sans-serif', fontSize: '15px',
                  fontWeight: 500, color: '#AAAAAA',
                  textDecoration: 'none', padding: '12px 0',
                  borderBottom: '1px solid #1A1A1A',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#E62B1E'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#AAAAAA'; }}
              >{link.label}</a>
            ))}
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a
                href={`mailto:${PROFILE.email}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: '#E62B1E', color: '#fff',
                  padding: '10px 20px', borderRadius: '4px',
                  fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 600,
                  textDecoration: 'none'
                }}
              >
                <Mail size={13} /> Get in Touch
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

const Footer = () => {
  const isMobile = useIsMobile();

  return (
    <footer id="contact" style={{
      background: '#0A0A0A',
      borderTop: '1px solid #1A1A1A',
      padding: isMobile ? '60px 0 32px' : '80px 0 40px'
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 48px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr 1fr',
          gap: isMobile ? '40px' : '60px',
          marginBottom: isMobile ? '40px' : '60px'
        }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '30px', height: '30px', background: '#E62B1E',
                borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '17px', color: '#fff' }}>K</span>
              </div>
              <span style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '16px', color: '#FFFFFF', letterSpacing: '2px' }}>KRUPAL CHAUDHARY</span>
            </div>
            <p style={{
              fontFamily: 'Inter, sans-serif', fontSize: '13px',
              color: '#4A4A4A', lineHeight: 1.75, margin: '0 0 20px 0', maxWidth: '300px'
            }}>
              25-year-old entrepreneur and AI Product Builder. Co-founder & CEO of Demaze Tech Studio. Upcoming TEDx Speaker at SRM University.
            </p>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(230,43,30,0.08)', border: '1px solid rgba(230,43,30,0.25)',
              borderRadius: '4px', padding: '6px 14px'
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E62B1E' }} />
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#E62B1E', fontWeight: 600, letterSpacing: '1.5px' }}>TEDx SRMIST · March 24, 2026</span>
            </div>
          </div>

          {/* Navigate */}
          {!isMobile && (
            <div>
              <h4 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '14px', color: '#FFFFFF', letterSpacing: '3px', margin: '0 0 20px 0' }}>Navigate</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {NAV_LINKS.map(link => (
                  <a key={link.href} href={link.href}
                    onClick={e => { e.preventDefault(); document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' }); }}
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#4A4A4A', textDecoration: 'none', transition: 'color 0.2s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#E62B1E'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#4A4A4A'; }}
                  >{link.label}</a>
                ))}
              </div>
            </div>
          )}

          {/* Connect */}
          <div>
            <h4 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '14px', color: '#FFFFFF', letterSpacing: '3px', margin: '0 0 20px 0' }}>Connect</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { icon: Mail, label: PROFILE.email, href: `mailto:${PROFILE.email}` },
                { icon: Phone, label: PROFILE.phone, href: `tel:${PROFILE.phone}` },
                { icon: Linkedin, label: 'linkedin.com/in/krupalchaudhary', href: PROFILE.linkedin, external: true },
                { icon: ExternalLink, label: PROFILE.website, href: `https://${PROFILE.website}`, external: true }
              ].map(({ icon: Icon, label, href, external }, i) => (
                <a key={i} href={href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#4A4A4A', textDecoration: 'none', transition: 'color 0.2s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#E62B1E'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#4A4A4A'; }}
                >
                  <Icon size={14} style={{ flexShrink: 0 }} />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', wordBreak: 'break-all' }}>{label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div style={{
          borderTop: '1px solid #141414', paddingTop: '28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '10px'
        }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#2E2E2E', margin: 0 }}>© 2026 Krupal Chaudhary. All rights reserved.</p>
          {!isMobile && (
            <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '12px', color: '#2E2E2E', margin: 0, letterSpacing: '1.5px' }}>
              "AI IS NOT THE FUTURE - IT IS THE OPERATING SYSTEM OF NOW"
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

const Portfolio = () => (
  <div style={{ background: '#0A0A0A' }}>
    <Navbar />
    <HeroSection />
    <StorySection />
    <ImpactSection />
    <RecognitionSection />
    <TEDxSection />
    <Footer />
  </div>
);

export default Portfolio;
